import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight, Lock, LayoutGrid, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { ROOMS, HERO_IMAGE, PHONE, PHONE_TEL, CATEGORY_META, BRAND_LOGO, MAIN_SITE_URL } from "@/showroomData";
import { CallBar } from "@/components/showroom/CallBar";
import { SpaDetailPanel } from "@/components/showroom/SpaDetailPanel";
import { LeadForm } from "@/components/showroom/LeadForm";
import { SHOWROOM } from "@/constants/testIds";

export default function Showroom() {
  const [spas, setSpas] = useState([]);
  const [entered, setEntered] = useState(false);
  const [roomIdx, setRoomIdx] = useState(0);
  const [selectedSpa, setSelectedSpa] = useState(null);
  const [lead, setLead] = useState(null);

  useEffect(() => {
    api.get("/spas").then((r) => setSpas(r.data)).catch(() => {});
  }, []);

  const room = ROOMS[roomIdx];
  const catCount = spas.filter((s) => s.category === room.category).length;

  const goto = (i) => {
    setSelectedSpa(null);
    setEntered(true);
    setRoomIdx((i + ROOMS.length) % ROOMS.length);
  };

  const openCta = (ctaType, spa) => setLead({ ctaType, spa });

  return (
    <div className="relative h-screen w-full overflow-hidden bg-[#16171c] text-white">
      <header className="fixed left-0 right-0 top-0 z-40 flex items-center justify-between border-b border-white/10 bg-black/40 px-4 py-3 backdrop-blur-xl sm:px-6">
        <div className="flex items-center gap-3">
          <img src={BRAND_LOGO} alt="Just Spas & Outdoors" className="h-9 rounded-md bg-white px-1.5 py-1 object-contain sm:h-10" />
          <div className="hidden text-[10px] uppercase tracking-widest text-slate-400 sm:block">Virtual Showroom</div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href={MAIN_SITE_URL}
            data-testid={SHOWROOM.exitLink}
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:bg-white/10 hover:text-white sm:text-sm"
            title="Exit to spasnewcastle.com.au"
          >
            <LogOut size={14} /> <span className="hidden sm:inline">Exit Showroom</span>
          </a>
          <Link
            to="/range"
            data-testid={SHOWROOM.rangeLink}
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200 transition-colors hover:bg-white/10 sm:text-sm"
          >
            <LayoutGrid size={14} /> Full Range
          </Link>
          <a href={`tel:${PHONE_TEL}`} className="hidden text-sm font-medium text-slate-200 hover:text-cyan-300 md:block">
            {PHONE}
          </a>
          <Link
            to="/admin"
            data-testid={SHOWROOM.adminLink}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-slate-400 transition-colors hover:text-white"
            title="Admin"
          >
            <Lock size={15} />
          </Link>
        </div>
      </header>

      <AnimatePresence>
        {!entered && (
          <motion.div
            className="grain absolute inset-0 z-30 flex flex-col items-center justify-center overflow-hidden px-6 text-center"
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{ duration: 0.7 }}
          >
            <div className="kenburns absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80" />
            <motion.div className="relative max-w-2xl" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }}>
              <span className="text-xs uppercase tracking-[0.4em] text-cyan-300">Gateshead · Newcastle</span>
              <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                Step inside your<br /><span className="text-cyan-400">dream backyard</span>
              </h1>
              <p className="mx-auto mt-5 max-w-lg text-base text-slate-300">
                Walk through cinematic rooms of spas, swim spas and urban pools — then browse our full Australian-made range.
              </p>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <button
                  onClick={() => setEntered(true)}
                  data-testid={SHOWROOM.enterButton}
                  className="group inline-flex items-center gap-3 rounded-full bg-cyan-500 px-8 py-4 font-display text-base font-semibold tracking-tight text-black transition-transform duration-200 hover:-translate-y-0.5 hover:bg-cyan-400"
                  style={{ boxShadow: "0 0 40px rgba(6,182,212,0.35)" }}
                >
                  Enter the Virtual Showroom
                  <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
                </button>
                <Link to="/range" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-4 text-sm font-medium text-white hover:bg-white/10">
                  <LayoutGrid size={16} /> Browse full range
                </Link>
              </div>
              <a href={`tel:${PHONE_TEL}`} className="mt-5 block text-sm text-slate-400">
                or call us now on <span className="text-white">{PHONE}</span>
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        <motion.div key={room.id} className="absolute inset-0" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
          <div className="kenburns absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${room.image})` }} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/50" />
          {room.hotspots.map((dot, i) => (
            <Link
              key={i}
              to={`/range?category=${room.category}&${dot.query}`}
              data-testid={`${SHOWROOM.dotPrefix}${room.id}-${i}`}
              className="group absolute z-20 -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${dot.x}%`, top: `${dot.y}%` }}
              title={dot.label}
            >
              <span className="hotspot-pulse flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-cyan-400/90">
                <span className="h-2 w-2 rounded-full bg-white" />
              </span>
              <span className="pointer-events-none absolute left-1/2 top-7 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/70 px-3 py-1 text-xs font-medium text-white opacity-0 backdrop-blur-md transition-opacity duration-200 group-hover:opacity-100">
                {dot.label}
              </span>
            </Link>
          ))}
        </motion.div>
      </AnimatePresence>

      <div className="pointer-events-none absolute bottom-28 left-4 z-20 sm:left-8">
        <motion.div key={room.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <span className="text-xs uppercase tracking-[0.3em] text-cyan-300">Room {roomIdx + 1} / {ROOMS.length}</span>
          <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight sm:text-4xl">{room.name}</h2>
          <p className="text-sm text-slate-300">{room.subtitle}</p>
          <Link
            to={`/range?category=${room.category}`}
            data-testid={SHOWROOM.browseRoomButton}
            className="pointer-events-auto mt-3 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-medium text-cyan-200 transition-colors hover:bg-cyan-500/20"
          >
            <LayoutGrid size={15} /> Browse all {catCount} {CATEGORY_META[room.category]?.label}
          </Link>
        </motion.div>
      </div>

      {entered && (
        <>
          <button onClick={() => goto(roomIdx - 1)} className="absolute left-3 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/40 backdrop-blur-md transition-colors hover:bg-black/70" data-testid="room-prev">
            <ChevronLeft size={22} />
          </button>
          <button onClick={() => goto(roomIdx + 1)} className="absolute right-3 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-black/40 backdrop-blur-md transition-colors hover:bg-black/70" data-testid="room-next">
            <ChevronRight size={22} />
          </button>
        </>
      )}

      <div className="absolute bottom-20 left-1/2 z-30 hidden w-full max-w-2xl -translate-x-1/2 justify-center gap-2 px-4 md:flex">
        {ROOMS.map((r, i) => (
          <button
            key={r.id}
            onClick={() => goto(i)}
            data-testid={`${SHOWROOM.roomNavPrefix}${r.id}`}
            className={`group relative h-16 flex-1 overflow-hidden rounded-lg border transition-all duration-200 ${i === roomIdx ? "border-cyan-400" : "border-white/10 opacity-60 hover:opacity-100"}`}
          >
            <img src={r.image} alt={r.name} className="h-full w-full object-cover" />
            <span className="absolute inset-0 flex items-end bg-gradient-to-t from-black/80 to-transparent p-1.5 text-left text-[10px] font-medium leading-tight">{r.name}</span>
          </button>
        ))}
      </div>

      <SpaDetailPanel spa={selectedSpa} onClose={() => setSelectedSpa(null)} onCta={openCta} />
      {lead && <LeadForm ctaType={lead.ctaType} spa={lead.spa} room={room.name} onClose={() => setLead(null)} />}
      <CallBar />
    </div>
  );
}
